import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./LiveOpsPages.css";

const pages = {
    maintenance: {
        slug: "game-maintenance",
        eyebrow: "Game Maintenance",
        title: "Game Maintenance for Stable Live Games, Updates, Fixes & Player Support",
        description: "Oengines Games LLP provides game maintenance support for live mobile, web, PC, and multiplayer games, including bug fixes, version updates, SDK updates, backend checks, performance review, and release support.",
        keywords: "game maintenance, live game support, mobile game maintenance, game bug fixes, game update support, multiplayer game maintenance",
        layout: "maintenance",
        stats: ["Bug Fixes", "SDK Updates", "Stability", "Support"],
        heroMode: "radar",
        services: [
            ["Bug Fixing Support", "Gameplay bugs, UI issues, crashes, login problems, store issues, device-specific problems, and release-blocking defects."],
            ["Version & SDK Updates", "Engine version checks, plugin updates, ad SDK updates, analytics SDK updates, store compliance updates, and API compatibility reviews."],
            ["Performance Review", "Memory, loading, frame drops, network issues, backend calls, asset weight, and device compatibility improvements."],
            ["Live Release Support", "Patch planning, release notes, build checks, regression review, and post-release monitoring support."]
        ],
        process: [
            ["Issue Audit", "Review bugs, logs, device reports, player complaints, and release priorities."],
            ["Fix Plan", "Prioritize critical crashes, store blockers, monetization issues, and gameplay defects."],
            ["Patch Cycle", "Implement fixes, test changes, prepare build notes, and verify affected flows."],
            ["Live Monitor", "Track post-release stability, player feedback, and next update tasks."]
        ],
        deliverables: ["Bug report review", "Patch notes", "Fix priority list", "SDK update notes", "Regression checklist", "Build support", "Stability notes", "Release follow-up"],
        faqs: [
            ["Can you maintain an already published game?", "Yes. We can support bug fixes, updates, SDK changes, compatibility checks, and release preparation."],
            ["Can maintenance include multiplayer games?", "Yes. We can review gameplay, backend calls, server behavior, login flow, and multiplayer-related issues."],
            ["Can you help after a store release?", "Yes. We can monitor issues, review crash/player feedback, and plan follow-up patches."]
        ]
    },
    content: {
        slug: "game-content-updates",
        eyebrow: "Game Content Updates",
        title: "Game Content Updates for Events, Levels, Characters, Rewards & LiveOps",
        description: "We plan and deliver game content updates for live games, including new levels, events, characters, skins, rewards, missions, tournaments, seasonal content, economy updates, and store refreshes.",
        keywords: "game content updates, LiveOps content updates, mobile game events, game level updates, seasonal game content, game rewards update",
        layout: "content",
        stats: ["Events", "Levels", "Rewards", "Skins"],
        heroMode: "calendar",
        services: [
            ["Seasonal Events", "Festival events, limited-time modes, tournament themes, daily missions, themed rewards, and event visuals."],
            ["Gameplay Content", "New levels, challenges, characters, enemies, modes, maps, items, card sets, and progression content."],
            ["Reward Refreshes", "Daily rewards, chests, battle pass items, skins, coin packs, achievements, and retention-focused reward loops."],
            ["Update Planning", "Content calendars, release cadence, event goals, asset lists, QA scope, and publish-ready update notes."]
        ],
        process: [
            ["Calendar Plan", "Define update cadence, event dates, content type, and production scope."],
            ["Asset & Feature List", "Plan art, UI, backend flags, rewards, missions, and store changes."],
            ["Build & QA", "Prepare update content, test flows, validate rewards, and review release blockers."],
            ["Live Refresh", "Publish updates, monitor response, and prepare next content cycle."]
        ],
        deliverables: ["Content calendar", "Event plan", "Reward plan", "Asset list", "Update notes", "QA checklist", "Store refresh plan", "LiveOps roadmap"],
        faqs: [
            ["Can you plan seasonal updates?", "Yes. We can plan event themes, reward loops, missions, visuals, and update schedules."],
            ["Can updates include new levels or game modes?", "Yes. We support level additions, modes, maps, items, characters, and progression content."],
            ["Can content updates improve retention?", "Yes. A planned cadence of events, rewards, missions, and store refreshes can improve repeat engagement."]
        ]
    },
    analytics: {
        slug: "game-analytics",
        eyebrow: "Game Analytics",
        title: "Game Analytics Setup for Retention, Economy, Funnels & Player Behavior",
        description: "Oengines helps game teams plan analytics events, funnels, dashboards, retention tracking, economy metrics, monetization tracking, level analytics, and LiveOps insights.",
        keywords: "game analytics, mobile game analytics, player behavior analytics, game retention metrics, game funnel tracking, LiveOps analytics",
        layout: "analytics",
        stats: ["Funnels", "Retention", "Economy", "Events"],
        heroMode: "dashboard",
        services: [
            ["Event Planning", "Tutorial, login, level, purchase, ad, reward, session, match, quest, failure, and progression event structures."],
            ["Player Funnels", "Install-to-login, tutorial completion, first purchase, ad engagement, level drop-off, and store conversion funnels."],
            ["Economy Metrics", "Currency sources/sinks, reward balance, item purchases, upgrade behavior, progression pacing, and monetization patterns."],
            ["LiveOps Dashboards", "KPI planning, daily tracking, event performance, cohort review, retention signals, and decision-ready reports."]
        ],
        process: [
            ["Metric Goals", "Define KPIs, business goals, game loops, player actions, and required visibility."],
            ["Event Map", "Create event names, parameters, funnels, and dashboard requirements."],
            ["Implementation Review", "Support developer handoff, validation, test events, and tracking checks."],
            ["Insight Loop", "Review results, identify friction, and plan LiveOps actions."]
        ],
        deliverables: ["Analytics event map", "Funnel plan", "KPI list", "Dashboard outline", "Economy metrics", "Validation checklist", "Tracking notes", "Insight report"],
        faqs: [
            ["Can you define analytics events for a game?", "Yes. We plan event names, parameters, funnels, metrics, and tracking requirements."],
            ["Can analytics help retention?", "Yes. Analytics can reveal tutorial friction, level drop-offs, reward gaps, and engagement patterns."],
            ["Can you track monetization and ads?", "Yes. We can plan purchase, ad, reward, store, currency, and economy tracking events."]
        ]
    },
    monetization: {
        slug: "game-monetization",
        eyebrow: "Game Monetization",
        title: "Game Monetization Strategy for IAP, Rewards, Stores, Offers & LiveOps",
        description: "We help game teams plan monetization systems for mobile and live games, including in-app purchases, reward loops, store offers, battle passes, currencies, pricing, bundles, and event monetization.",
        keywords: "game monetization, mobile game monetization, in app purchase strategy, game store offers, game economy monetization, LiveOps monetization",
        layout: "monetization",
        stats: ["IAP", "Offers", "Rewards", "Economy"],
        heroMode: "store",
        services: [
            ["Store Design Strategy", "Offer cards, bundles, currency packs, item shops, limited-time offers, pricing tiers, and store refresh planning."],
            ["Reward Economy", "Soft currency, hard currency, chests, battle passes, daily rewards, progression rewards, and currency source/sink balance."],
            ["LiveOps Offers", "Event offers, seasonal bundles, tournament rewards, comeback offers, starter packs, and segmentation ideas."],
            ["Conversion Review", "Store flow, UI clarity, purchase motivation, offer timing, reward visibility, and monetization analytics planning."]
        ],
        process: [
            ["Economy Audit", "Review progression, rewards, currency, player motivation, and current store flow."],
            ["Offer Design", "Plan packs, bundles, pricing, reward value, timing, and event hooks."],
            ["UI & Tracking", "Define store UI needs, purchase events, ad events, and conversion metrics."],
            ["Live Tuning", "Review performance and adjust offers, rewards, pricing, and cadence."]
        ],
        deliverables: ["Monetization plan", "Store offer map", "Reward economy notes", "IAP structure", "Battle pass ideas", "Pricing guidance", "Tracking events", "Live tuning notes"],
        faqs: [
            ["Can you plan in-app purchases for games?", "Yes. We can plan packs, bundles, currencies, pricing tiers, and store presentation."],
            ["Can monetization work without hurting retention?", "Yes. The goal is to balance reward value, progression, player trust, and conversion opportunities."],
            ["Can you improve an existing game store?", "Yes. We can review offer clarity, timing, UI, analytics, and reward value."]
        ]
    },
    ads: {
        slug: "game-ad-integration",
        eyebrow: "Game Ad Integration",
        title: "Game Ad Integration for Rewarded Ads, Interstitials, Banners & Mediation",
        description: "Oengines supports game ad integration planning for rewarded ads, interstitials, banners, mediation, ad placement strategy, player-friendly ad timing, analytics, and monetization review.",
        keywords: "game ad integration, rewarded ads game, mobile game ads, ad mediation for games, interstitial ads game, ad monetization game",
        layout: "ads",
        stats: ["Rewarded", "Interstitial", "Mediation", "Revenue"],
        heroMode: "adstack",
        services: [
            ["Rewarded Ads", "Rewarded video placement, reward value, cooldowns, player motivation, economy impact, and ad event tracking."],
            ["Interstitial Ads", "Level-end ads, session flow, fail states, frequency capping, player-safe timing, and retention-aware placement."],
            ["Mediation Planning", "Ad network setup direction, waterfall/bidding considerations, placement IDs, testing flows, and release checks."],
            ["Ad Analytics", "Ad impressions, clicks, rewards granted, eCPM, fill rate, placement performance, and monetization dashboard needs."]
        ],
        process: [
            ["Placement Strategy", "Decide ad formats, trigger points, player value, reward type, and frequency."],
            ["Integration Plan", "Prepare SDK, placement IDs, callbacks, reward validation, and test flow requirements."],
            ["QA & Compliance", "Test ad loading, rewards, fallback behavior, store rules, and device behavior."],
            ["Revenue Review", "Track performance, adjust placement timing, and improve reward/ad balance."]
        ],
        deliverables: ["Ad placement plan", "Rewarded ad flow", "Interstitial timing", "Mediation notes", "Placement ID checklist", "QA scenarios", "Analytics events", "Revenue review notes"],
        faqs: [
            ["Can you plan rewarded ads for games?", "Yes. We can plan reward value, placement timing, cooldowns, callbacks, and analytics events."],
            ["Can ads be placed without hurting players?", "Yes. We plan frequency, timing, and value so ads support monetization without damaging gameplay flow."],
            ["Can you support ad mediation setup?", "Yes. We can help plan mediation requirements, placement IDs, testing, and release checks."]
        ]
    },
    publishing: {
        slug: "game-store-publishing",
        eyebrow: "Game Store Publishing",
        title: "Game Store Publishing Support for App Store, Google Play & Release Readiness",
        description: "We support game store publishing for mobile games, including store listing assets, metadata, screenshots, compliance checks, build readiness, release notes, testing tracks, and launch support.",
        keywords: "game store publishing, mobile game publishing, Google Play game publishing, App Store game publishing, game launch support, store listing optimization",
        layout: "publishing",
        stats: ["Listings", "Screenshots", "Compliance", "Launch"],
        heroMode: "launch",
        services: [
            ["Store Listing Support", "Game title, short description, long description, keywords, screenshots, feature graphics, videos, and category review."],
            ["Build Readiness", "Version checks, package details, SDK compliance, permissions review, testing tracks, release notes, and build validation."],
            ["Compliance Review", "Privacy, data safety, age rating, content rating, ads declaration, IAP setup, and platform policy readiness."],
            ["Launch Support", "Pre-launch checklist, rollout plan, store submission support, rejection review, update support, and post-launch fixes."]
        ],
        process: [
            ["Store Audit", "Review game build, assets, metadata, compliance needs, and launch requirements."],
            ["Listing Prep", "Prepare descriptions, screenshots, graphics, categories, and release notes."],
            ["Submission Check", "Validate build, policies, permissions, IAP/ads declarations, and testing track setup."],
            ["Launch Follow-Up", "Support review feedback, release rollout, store updates, and post-launch issues."]
        ],
        deliverables: ["Store listing checklist", "Metadata draft", "Screenshot plan", "Release notes", "Compliance checklist", "Build readiness notes", "Submission support", "Launch follow-up"],
        faqs: [
            ["Can you help publish games on Google Play?", "Yes. We can support listing preparation, build checks, compliance review, and release readiness."],
            ["Can you help with App Store submission?", "Yes. We can prepare metadata, screenshots, compliance notes, release notes, and submission support."],
            ["Can you help after rejection?", "Yes. We can review rejection reasons, update required assets or settings, and support resubmission planning."]
        ]
    }
};

function LiveOpsPage({ page }) {
    const data = pages[page];
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": data.eyebrow,
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": data.eyebrow,
        "areaServed": "Worldwide",
        "description": data.description
    };

    return (
        <>
            <Helmet>
                <title>{data.title} | Oengines Games LLP</title>
                <meta name="description" content={data.description} />
                <meta name="keywords" content={data.keywords} />
                <link rel="canonical" href={`https://www.oengines.com/${data.slug}`} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className={`liveops-page liveops-${data.layout}`}>
                <section className="liveops-hero">
                    <div className="container">
                        <div className="liveops-hero-grid">
                            <div className="liveops-copy">
                                <span>{data.eyebrow}</span>
                                <h1>{data.title}</h1>
                                <p>{data.description}</p>
                                <div className="liveops-actions">
                                    <a href="/contact" className="as-btn">Discuss LiveOps <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="liveops-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className={`liveops-visual ${data.heroMode}`} aria-hidden="true">
                                {data.heroMode === "dashboard" ? (
                                    <div className="liveops-analytics-board">
                                        <div className="analytics-kpi primary">
                                            <span>Retention</span>
                                            <strong>42%</strong>
                                        </div>
                                        <div className="analytics-kpi">
                                            <span>ARPDAU</span>
                                            <strong>+18%</strong>
                                        </div>
                                        <div className="analytics-chart">
                                            <i /><i /><i /><i /><i /><i />
                                        </div>
                                        <div className="analytics-funnel">
                                            {data.stats.map((item) => <span key={item}>{item}</span>)}
                                        </div>
                                    </div>
                                ) : data.heroMode === "store" ? (
                                    <div className="liveops-store-board">
                                        <div className="store-offer featured">
                                            <span>Best Value</span>
                                            <strong>Starter Pack</strong>
                                            <em>$4.99</em>
                                        </div>
                                        <div className="store-currency">
                                            <i /><i /><i />
                                            <strong>Currency Flow</strong>
                                        </div>
                                        <div className="store-offer">
                                            <span>Event Bundle</span>
                                            <strong>2.4x</strong>
                                            <em>Reward Value</em>
                                        </div>
                                        <div className="store-metrics">
                                            {data.stats.map((item) => <span key={item}>{item}</span>)}
                                        </div>
                                    </div>
                                ) : data.heroMode === "launch" ? (
                                    <div className="liveops-launch-board">
                                        <div className="launch-store-preview">
                                            <div className="store-preview-top">
                                                <div className="listing-icon" />
                                                <div>
                                                    <span>Store Listing</span>
                                                    <strong>Game Launch Build</strong>
                                                    <p>Metadata, screenshots, compliance, and rollout assets prepared for review.</p>
                                                </div>
                                            </div>
                                            <div className="store-screenshot-row">
                                                <i /><i /><i />
                                            </div>
                                        </div>
                                        <div className="launch-release-panel">
                                            <div className="release-ring">
                                                <span>Ready</span>
                                            </div>
                                            <strong>Submission Ready</strong>
                                            <div className="review-progress"><i /></div>
                                        </div>
                                        <div className="launch-checks">
                                            {data.stats.map((item, index) => (
                                                <span key={item}><i>{String(index + 1).padStart(2, "0")}</i>{item}</span>
                                            ))}
                                        </div>
                                        <div className="launch-rollout-strip">
                                            <span>Internal Test</span>
                                            <span>Closed Track</span>
                                            <span>Store Review</span>
                                            <span>Production</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="liveops-core">
                                        {data.stats.map((item) => <span key={item}>{item}</span>)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="liveops-services space">
                    <div className="container-fluid px-xxl-5">
                        <div className="liveops-heading">
                            <span>Game Live Operations</span>
                            <h2>Support Systems That Keep Games Stable, Updated, Measurable, and Monetized</h2>
                        </div>
                        <div className="liveops-service-grid">
                            {data.services.map(([title, text], index) => (
                                <article key={title} className={index === 0 ? "featured" : ""}>
                                    <strong>{String(index + 1).padStart(2, "0")}</strong>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="liveops-process space">
                    <div className="container">
                        <div className="liveops-process-grid">
                            <div className="liveops-process-title">
                                <span>Delivery Flow</span>
                                <h2>{data.eyebrow} Workflow</h2>
                                <p>Every LiveOps page is planned around production cadence, release risk, player impact, data visibility, and post-launch improvement.</p>
                            </div>
                            <div className="liveops-process-list">
                                {data.process.map(([title, text], index) => (
                                    <article key={title}>
                                        <strong>{String(index + 1).padStart(2, "0")}</strong>
                                        <div>
                                            <h3>{title}</h3>
                                            <p>{text}</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="liveops-deliverables space">
                    <div className="container">
                        <div className="liveops-deliverable-grid">
                            <div>
                                <span>Deliverables</span>
                                <h2>Practical LiveOps Assets, Plans, and Handoff Notes</h2>
                                <p>Deliverables are structured so product, art, development, QA, marketing, and release teams can act on them.</p>
                            </div>
                            <div className="liveops-deliverable-list">
                                {data.deliverables.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="liveops-faq space">
                    <div className="container">
                        <div className="liveops-faq-grid">
                            <div className="liveops-faq-title">
                                <span>FAQ</span>
                                <h2>{data.eyebrow} Questions</h2>
                                <a href="/contact" className="as-btn">Start Support Plan <i className="fa-regular fa-arrow-right ms-2"></i></a>
                            </div>
                            <div className="liveops-faq-list">
                                {data.faqs.map(([question, answer]) => (
                                    <article key={question}>
                                        <h3>{question}</h3>
                                        <p>{answer}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

export const GameMaintenance = () => <LiveOpsPage page="maintenance" />;
export const GameContentUpdates = () => <LiveOpsPage page="content" />;
export const GameAnalytics = () => <LiveOpsPage page="analytics" />;
export const GameMonetization = () => <LiveOpsPage page="monetization" />;
export const GameAdIntegration = () => <LiveOpsPage page="ads" />;
export const GameStorePublishing = () => <LiveOpsPage page="publishing" />;
